import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useAppTheme } from "@/hooks/useAppTheme";

interface MetadataSectionProps {
  metadata?: Record<string, string>;
}

/**
 * Human-readable descriptions for known metadata keys used by the task engine.
 */
const KNOWN_KEY_DESCRIPTIONS: Record<string, string> = {
  carrier_tracking_required: "Whether tasks from this template require carrier-level tracking.",
  notification_type: "Who receives the notification (e.g. carrier, internal, client).",
  icd_trigger_based: "Indicates the task is triggered by specific ICD-10 diagnosis codes.",
  verification_type: "The type of verification required (e.g. eligibility, cob).",
  critical_task: "Marks this as a high-priority task that requires immediate attention.",
  documentation_type: "The specific type of documentation required (e.g. itemized_billings, check_registry).",
  update_scenario: "Indicates this template handles update scenarios for existing claims.",
  funding_related: "Task is related to claim funding or prompt pay processing.",
  data_quality_issue: "Task addresses a data quality or placeholder record issue.",
  placeholder_type: "The type of placeholder record to be completed (e.g. client, policy).",
};

export const MetadataSection = ({ metadata }: MetadataSectionProps) => {
  const { isDarkMode } = useAppTheme();
  const entries = Object.entries(metadata ?? {});

  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No metadata configured.
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: "40%" }}>Key</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map(([key, value]) => {
            const description = KNOWN_KEY_DESCRIPTIONS[key];
            return (
              <TableRow key={key} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                      {key}
                    </Typography>
                    {description && (
                      <Tooltip title={description} placement="top" arrow>
                        <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.8rem",
                      bgcolor: isDarkMode ? "grey.900" : "grey.100",
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.5,
                      display: "inline-block",
                    }}
                  >
                    {value}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
